import { type AxiosInstance, type AxiosError, HttpStatusCode } from "axios";
import useSWR, { type KeyedMutator } from "swr";
import type { GetInheritanceReuseCitiesAPIResponse } from "@/features/inheritanceReuse/types";
import { SendToTrackingService } from "@/api/handlers/errors";

const ENDPOINT = "/inheritance-reuse/cities";

interface IResponse {
  data: GetInheritanceReuseCitiesAPIResponse | undefined;
  isLoading: boolean;
  mutate: KeyedMutator<GetInheritanceReuseCitiesAPIResponse>;
  error: AxiosError | undefined;
}

/**
 * 相続後リユース候補の市区町村別件数を取得する（都道府県依存）。
 * フィルタの市区町村セレクト（件数表示）に用いる。都道府県未選択（undefined）時は発火しない。
 * prefectureName（camelCase）は apiClient の case 変換で prefecture_name に変換される。
 * @param {AxiosInstance} apiClient JWT 認証付き apiClient
 * @param {string | undefined} prefectureName 都道府県名（必須クエリ）
 * @returns {IResponse}
 */
export const GetInheritanceReuseCitiesAPI = (
  apiClient: AxiosInstance,
  prefectureName: string | undefined
): IResponse => {
  const fetcher = async (
    url: string,
    name: string
  ): Promise<GetInheritanceReuseCitiesAPIResponse> => {
    const { data } = await apiClient.get<GetInheritanceReuseCitiesAPIResponse>(
      url,
      { params: { prefectureName: name } }
    );
    return data;
  };

  const { data, isLoading, mutate, error } = useSWR<
    GetInheritanceReuseCitiesAPIResponse,
    AxiosError
  >(
    prefectureName === undefined || prefectureName === ""
      ? null
      : [ENDPOINT, prefectureName],
    async ([url, name]: [string, string]) => await fetcher(url, name),
    { revalidateOnFocus: false }
  );

  if (
    error &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    SendToTrackingService(error);
    return { data: undefined, isLoading, mutate, error };
  }

  return { data, isLoading, mutate, error };
};
